import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CookieIcon from '@mui/icons-material/Cookie';
import SettingsIcon from '@mui/icons-material/Settings';

const COOKIE_CONSENT_KEY = 'familyforlife_cookie_consent';

const defaultConsent = {
  essential: true, // Always true, cannot be disabled
  functional: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const CookieConsentBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);

  useEffect(() => {
    // Check if consent has been given
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (storedConsent) {
      try {
        const parsed = JSON.parse(storedConsent);
        setConsent(parsed);
      } catch {
        // Invalid stored consent, show banner
        setShowBanner(true);
      }
    } else {
      // No consent stored, show banner
      setShowBanner(true);
    }
  }, []);

  const saveConsent = (newConsent) => {
    const consentWithTimestamp = {
      ...newConsent,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentWithTimestamp));
    setConsent(consentWithTimestamp);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleAcceptEssential = () => {
    saveConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  const handleConsentChange = (category) => (event) => {
    if (category === 'essential') return; // Essential cannot be disabled
    setConsent((prev) => ({
      ...prev,
      [category]: event.target.checked,
    }));
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  // Cookie categories with descriptions
  const cookieCategories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for the website to function. These cannot be disabled.',
      disabled: true,
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Enable enhanced functionality and personalization, such as remembering your theme preference.',
      disabled: false,
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how visitors use our website to improve it.',
      disabled: false,
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements. Currently not in use.',
      disabled: false,
    },
  ];

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <>
      {/* Cookie Consent Banner */}
      <Slide direction="up" in={showBanner && !showSettings} mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.snackbar + 100,
            p: { xs: 2, sm: 3 },
            borderRadius: 0,
            borderTop: `3px solid ${theme.palette.primary.main}`,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              maxWidth: 'lg',
              mx: 'auto',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
              <CookieIcon color="primary" sx={{ fontSize: 28, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  We value your privacy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We use cookies to enhance your browsing experience, provide personalized
                  content, and analyze our traffic. By clicking "Accept All", you consent to
                  our use of cookies. Read our{' '}
                  <Link component={RouterLink} to="/cookies" color="primary">
                    Cookie Policy
                  </Link>{' '}
                  and{' '}
                  <Link component={RouterLink} to="/privacy" color="primary">
                    Privacy Policy
                  </Link>{' '}
                  for more information.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleAcceptEssential}
                size={isMobile ? 'medium' : 'small'}
              >
                Essential Only
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={openSettings}
                size={isMobile ? 'medium' : 'small'}
              >
                Customize
              </Button>
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                size={isMobile ? 'medium' : 'small'}
              >
                Accept All
              </Button>
            </Box>
          </Box>
        </Paper>
      </Slide>

      {/* Cookie Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CookieIcon color="primary" />
          Cookie Preferences
          <IconButton
            onClick={() => setShowSettings(false)}
            sx={{ ml: 'auto' }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Customize your cookie preferences below. Essential cookies are always enabled
            as they are necessary for the website to function properly.
          </Typography>

          <FormGroup>
            {cookieCategories.map((category, index) => (
              <React.Fragment key={category.id}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Box sx={{ py: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={consent[category.id]}
                        onChange={handleConsentChange(category.id)}
                        disabled={category.disabled}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight={600}>
                        {category.name}
                      </Typography>
                    }
                    sx={{ mb: 0.5 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 6 }}
                  >
                    {category.description}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </FormGroup>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              For more details about the cookies we use and how we handle your data,
              please read our{' '}
              <Link component={RouterLink} to="/cookies" color="primary">
                Cookie Policy
              </Link>{' '}
              and{' '}
              <Link component={RouterLink} to="/privacy" color="primary">
                Privacy Policy
              </Link>.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleAcceptEssential} variant="outlined">
            Essential Only
          </Button>
          <Button onClick={handleAcceptAll} variant="outlined">
            Accept All
          </Button>
          <Button onClick={handleSavePreferences} variant="contained">
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Export a function to programmatically open cookie settings
// This can be called from footer or other components
export const openCookieSettings = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
};

// Export a function to check current consent
export const getCookieConsent = () => {
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

// Export a function to check if a specific cookie category is allowed
export const isCookieCategoryAllowed = (category) => {
  const consent = getCookieConsent();
  if (!consent) return category === 'essential';
  return consent[category] === true;
};

export default CookieConsentBanner;
